import React, { useState, useEffect, useContext } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { AuthContext } from '../../../contexts/AuthContext';
import { taxAdjustmentsService } from '../../../services/taxAdjustmentsService';

const TaxAdjustments = () => {
  const { user, userProfile } = useContext(AuthContext);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);

  const [formData, setFormData] = useState({
    adjustment_type: 'vat_correction',
    reference_number: '',
    description: '',
    adjustment_date: new Date()?.toISOString()?.split('T')?.[0],
    tax_period_start: '',
    tax_period_end: '',
    original_amount: '',
    adjustment_amount: '',
    adjusted_amount: '',
    tax_rate: '',
    tax_code: '',
    reason_code: ''
  });

  const [filters, setFilters] = useState({
    adjustmentType: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  });

  const [calculator, setCalculator] = useState({
    originalAmount: '',
    oldRate: '',
    newRate: '',
    calculation: null
  });

  const adjustmentTypes = [
    { value: 'vat_correction', label: 'VAT Correction' },
    { value: 'tax_refund', label: 'Tax Refund' },
    { value: 'penalty_adjustment', label: 'Penalty Adjustment' },
    { value: 'rate_change', label: 'Rate Change' },
    { value: 'compliance_adjustment', label: 'Compliance Adjustment' }
  ];

  const taxCodes = [
    { value: 'VAT-STD', label: 'VAT Standard Rate' },
    { value: 'VAT-RED', label: 'VAT Reduced Rate' },
    { value: 'VAT-ZERO', label: 'VAT Zero Rate' },
    { value: 'VAT-EX', label: 'VAT Exempt' },
    { value: 'CORP-TAX', label: 'Corporate Tax' },
    { value: 'WHT', label: 'Withholding Tax' }
  ];

  const reasonCodes = [
    { value: 'RATE_CHANGE', label: 'Tax Rate Change' },
    { value: 'CALC_ERROR', label: 'Calculation Error' },
    { value: 'COMPLIANCE', label: 'Compliance Adjustment' },
    { value: 'REFUND', label: 'Tax Refund' },
    { value: 'PENALTY', label: 'Penalty Assessment' },
    { value: 'AUDIT', label: 'Audit Adjustment' }
  ];

  useEffect(() => {
    if (userProfile?.organizations?.[0]?.id) {
      loadAdjustments();
    }
  }, [userProfile]);

  const loadAdjustments = async () => {
    setLoading(true);
    try {
      const organizationId = userProfile?.organizations?.[0]?.id;
      const result = await taxAdjustmentsService?.getTaxAdjustments(organizationId, {
        page: 1,
        limit: 50,
        ...filters
      });

      if (result?.data) {
        setAdjustments(result?.data);
      }
    } catch (error) {
      console.error('Load adjustments error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    try {
      const result = await taxAdjustmentsService?.calculateVATAdjustment(
        parseFloat(calculator?.originalAmount),
        parseFloat(calculator?.oldRate),
        parseFloat(calculator?.newRate)
      );

      if (result?.error) {
        alert('Calculation error: ' + result?.error);
        return;
      }

      setCalculator({ ...calculator, calculation: result });
      
      // Auto-fill form with calculated values
      setFormData({
        ...formData,
        original_amount: result?.originalAmount?.toString(),
        adjustment_amount: result?.adjustmentAmount?.toString(),
        adjusted_amount: result?.adjustedAmount?.toString(),
        tax_rate: result?.newRate?.toString()
      });
    } catch (error) {
      console.error('Calculate error:', error);
      alert('Failed to calculate VAT adjustment');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!user?.id || !userProfile?.organizations?.[0]?.id) return;

    setCreating(true);
    try {
      const organizationId = userProfile?.organizations?.[0]?.id;
      
      const result = await taxAdjustmentsService?.createTaxAdjustment({
        organization_id: organizationId,
        created_by: user?.id,
        ...formData,
        original_amount: parseFloat(formData?.original_amount),
        adjustment_amount: parseFloat(formData?.adjustment_amount),
        adjusted_amount: parseFloat(formData?.adjusted_amount),
        tax_rate: parseFloat(formData?.tax_rate)
      });

      if (result?.error) {
        alert('Error creating adjustment: ' + result?.error);
        return;
      }

      // Reset form
      setFormData({
        adjustment_type: 'vat_correction',
        reference_number: '',
        description: '',
        adjustment_date: new Date()?.toISOString()?.split('T')?.[0],
        tax_period_start: '',
        tax_period_end: '',
        original_amount: '',
        adjustment_amount: '',
        adjusted_amount: '',
        tax_rate: '',
        tax_code: '',
        reason_code: ''
      });
      setCalculator({
        originalAmount: '',
        oldRate: '',
        newRate: '',
        calculation: null
      });
      setShowForm(false);

      // Reload data
      await loadAdjustments();

      alert('Tax adjustment created successfully!');
    } catch (error) {
      console.error('Create adjustment error:', error);
      alert('Failed to create tax adjustment');
    } finally {
      setCreating(false);
    }
  };

  const handleApprove = async (adjustmentId) => {
    if (!user?.id) return;

    try {
      const result = await taxAdjustmentsService?.approveTaxAdjustment(adjustmentId, user?.id);
      if (result?.error) {
        alert('Error approving adjustment: ' + result?.error);
        return;
      }

      await loadAdjustments();
      alert('Tax adjustment approved successfully!');
    } catch (error) {
      console.error('Approve error:', error);
      alert('Failed to approve tax adjustment');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Tax Adjustments</h2>
          <p className="text-muted-foreground mt-1">VAT corrections, tax refunds, and compliance adjustments</p>
        </div>
        <div className="flex space-x-3 mt-4 sm:mt-0">
          <Button 
            onClick={() => setShowCalculator(!showCalculator)}
            variant="outline"
            iconName="Calculator"
            iconPosition="left"
          >
            VAT Calculator
          </Button>
          <Button 
            onClick={() => setShowForm(!showForm)}
            iconName="Plus"
            iconPosition="left"
          >
            New Adjustment
          </Button>
        </div>
      </div>
      {/* VAT Calculator */}
      {showCalculator && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">VAT Adjustment Calculator</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <Input
              label="Original Amount"
              type="number"
              step="0.01"
              value={calculator?.originalAmount}
              onChange={(e) => setCalculator({...calculator, originalAmount: e?.target?.value})}
              placeholder="0.00"
            />
            <Input
              label="Old Tax Rate (%)"
              type="number"
              step="0.01"
              value={calculator?.oldRate}
              onChange={(e) => setCalculator({...calculator, oldRate: e?.target?.value})}
              placeholder="0.00"
            />
            <Input
              label="New Tax Rate (%)"
              type="number"
              step="0.01"
              value={calculator?.newRate}
              onChange={(e) => setCalculator({...calculator, newRate: e?.target?.value})}
              placeholder="0.00"
            />
          </div>

          <Button onClick={handleCalculate} iconName="Calculator" className="mb-4">
            Calculate
          </Button>

          {calculator?.calculation && (
            <div className="bg-muted/20 border border-border rounded-lg p-4">
              <h4 className="font-medium mb-3">Calculation Results</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Original Amount:</span>
                  <p className="font-medium">${calculator?.calculation?.originalAmount?.toFixed(2)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Old VAT:</span>
                  <p className="font-medium">${calculator?.calculation?.oldVat?.toFixed(2)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">New VAT:</span>
                  <p className="font-medium">${calculator?.calculation?.newVat?.toFixed(2)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Adjustment:</span>
                  <p className={`font-medium ${calculator?.calculation?.adjustmentAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${calculator?.calculation?.adjustmentAmount?.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Select
            value={filters?.adjustmentType}
            onChange={(value) => setFilters({...filters, adjustmentType: value})}
            placeholder="Filter by type"
            options={[
              { value: '', label: 'All Types' },
              ...adjustmentTypes
            ]}
          />
          <Input
            type="date"
            value={filters?.dateFrom}
            onChange={(e) => setFilters({...filters, dateFrom: e?.target?.value})}
            placeholder="From date"
          />
          <Input
            type="date"
            value={filters?.dateTo}
            onChange={(e) => setFilters({...filters, dateTo: e?.target?.value})}
            placeholder="To date"
          />
          <div className="flex space-x-2">
            <Input
              value={filters?.search}
              onChange={(e) => setFilters({...filters, search: e?.target?.value})}
              placeholder="Search adjustments..."
              className="flex-1"
            />
            <Button onClick={loadAdjustments} variant="outline" iconName="Search">
              Search
            </Button>
          </div>
        </div>
      </div>
      {/* Tax Adjustment Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">New Tax Adjustment</h3>
          
          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <Select
              label="Adjustment Type"
              value={formData?.adjustment_type}
              onChange={(value) => setFormData({...formData, adjustment_type: value})}
              options={adjustmentTypes}
              required
            />
            <Input
              label="Reference Number"
              value={formData?.reference_number}
              onChange={(e) => setFormData({...formData, reference_number: e?.target?.value})}
              placeholder="Enter reference number"
              required
            />
            <Input
              label="Adjustment Date"
              type="date"
              value={formData?.adjustment_date}
              onChange={(e) => setFormData({...formData, adjustment_date: e?.target?.value})}
              required
            />
          </div>

          <div className="mb-6">
            <Input
              label="Description"
              value={formData?.description}
              onChange={(e) => setFormData({...formData, description: e?.target?.value})}
              placeholder="Enter adjustment description"
              required
            />
          </div>

          {/* Tax Period */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <Input
              label="Tax Period Start"
              type="date"
              value={formData?.tax_period_start}
              onChange={(e) => setFormData({...formData, tax_period_start: e?.target?.value})}
              required
            />
            <Input
              label="Tax Period End"
              type="date"
              value={formData?.tax_period_end}
              onChange={(e) => setFormData({...formData, tax_period_end: e?.target?.value})}
              required
            />
          </div>

          {/* Amounts */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Input
              label="Original Amount"
              type="number"
              step="0.01"
              value={formData?.original_amount}
              onChange={(e) => setFormData({...formData, original_amount: e?.target?.value})}
              placeholder="0.00"
              required
            />
            <Input
              label="Adjustment Amount"
              type="number"
              step="0.01"
              value={formData?.adjustment_amount}
              onChange={(e) => setFormData({...formData, adjustment_amount: e?.target?.value})}
              placeholder="0.00"
              required
            />
            <Input
              label="Adjusted Amount"
              type="number"
              step="0.01"
              value={formData?.adjusted_amount}
              onChange={(e) => setFormData({...formData, adjusted_amount: e?.target?.value})}
              placeholder="0.00"
              required
            />
          </div>

          {/* Tax Details */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <Input
              label="Tax Rate (%)"
              type="number"
              step="0.01"
              value={formData?.tax_rate}
              onChange={(e) => setFormData({...formData, tax_rate: e?.target?.value})}
              placeholder="0.00"
            />
            <Select
              label="Tax Code"
              value={formData?.tax_code}
              onChange={(value) => setFormData({...formData, tax_code: value})}
              options={taxCodes}
              placeholder="Select tax code"
            />
            <Select
              label="Reason Code"
              value={formData?.reason_code}
              onChange={(value) => setFormData({...formData, reason_code: value})}
              options={reasonCodes}
              placeholder="Select reason"
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              onClick={() => setShowForm(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={creating}
              iconName={creating ? "Loader2" : "Save"}
              className={creating ? "animate-spin" : ""}
            >
              {creating ? 'Creating...' : 'Create Adjustment'}
            </Button>
          </div>
        </form>
      )}
      {/* Adjustments List */}
      <div className="bg-card border border-border rounded-lg">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Tax Adjustments</h3>
        </div>
        
        {adjustments?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Calculator" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Tax Adjustments</h3>
            <p className="text-muted-foreground">Create your first tax adjustment to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {adjustments?.map((adjustment) => (
              <div key={adjustment?.id} className="px-6 py-4 hover:bg-muted/20">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="font-medium text-foreground">{adjustment?.reference_number}</span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {adjustment?.adjustment_type?.replace('_', ' ')?.toUpperCase()}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {new Date(adjustment?.adjustment_date)?.toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-foreground mb-1">{adjustment?.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                      <span>Created by {adjustment?.created_by?.full_name}</span>
                      <span>•</span>
                      <span>Original: ${adjustment?.original_amount}</span>
                      <span>•</span>
                      <span className={adjustment?.adjustment_amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                        Adjustment: ${adjustment?.adjustment_amount}
                      </span>
                      {adjustment?.tax_rate && (
                        <>
                          <span>•</span>
                          <span>Rate: {adjustment?.tax_rate}%</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {!adjustment?.approved_by && (
                      <Button
                        onClick={() => handleApprove(adjustment?.id)}
                        size="sm"
                        iconName="CheckCircle"
                        variant="outline"
                      >
                        Approve
                      </Button>
                    )}
                    {adjustment?.approved_by && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        APPROVED
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaxAdjustments;